import React, { useCallback, useEffect, useRef, useState } from "react";

import useEsTelefono from "../../../../utils/esTelefono";
import { ApiTareasPersonal } from "../../../../services/v2/classPortal.service";
import { AlumnoDelQuiz, ApiQuizPersonal, EstadoQuizPersonal, IntentoPersonal, IntentosDeAlumno, ResultadosQuiz } from "../../../../services/v2/lmsQuiz.service";
import { Aviso, Avatar, exito, Icono, LineaAviso, mensajeDeError, problema, useAlVolver } from "../comun";
import { esNotaBaja, puntosTxt } from "../tareas/comunTareas";
import { TIPO_PREGUNTA } from "./editorQuiz";
import { RespuestaVista } from "./respuestaVista";
import "../tareas/tareas.css";
import "./quiz.css";

/*
	Resultados del quiz (personal, Parte D). Dos vistas: Alumnos (como Calificar: lista a la izquierda, los intentos al centro con
	cada respuesta, la nota a la derecha) y Preguntas (% de acierto de cada una, la más fallada arriba). Lo automático lo califica
	el servidor; aquí solo se califican las preguntas de Párrafo, con Devolver Y Siguiente. Cuenta el mejor intento.
	Carga al abrir, al volver a la pestaña y con Actualizar; sin sondeos.
*/

const GRUPOS: { estado: EstadoQuizPersonal; titulo: string }[] = [
	{ estado: "por_calificar", titulo: "Por Calificar" }, { estado: "calificada", titulo: "Calificadas" }, { estado: "devuelta", titulo: "Devueltas" },
	{ estado: "en_curso", titulo: "Contestando Ahora" }, { estado: "sin_intentar", titulo: "Sin Intentar" }, { estado: "falta", titulo: "No Lo Hicieron" },
]

const pctTxt = (n: number | null | undefined): string => (n === null || n === undefined ? "–" : String(Math.round(n)) + " %")

interface Props
{
	apiQ: ApiQuizPersonal
	apiT: ApiTareasPersonal
	postId: number
	cursoTitulo: string
	onCerrar: () => void
	/** Solo quizzes propios del horario: abre Editar Quiz. */
	onEditar: () => void
}

const ResultadosQuizPantalla: React.FC<Props> = ({ apiQ, apiT, postId, cursoTitulo, onCerrar, onEditar }) => {
	const telefono = useEsTelefono()
	const [res, setRes] = useState<ResultadosQuiz | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(null)
	const [vistaGeneral, setVistaGeneral] = useState<"alumnos" | "preguntas">("alumnos")
	const [vista, setVista] = useState<"lista" | "detalle">("lista")
	const [sel, setSel] = useState<number | null>(null)
	const [det, setDet] = useState<IntentosDeAlumno | null>(null)
	const [intento, setIntento] = useState<number | null>(null)
	const [puntos, setPuntos] = useState<Record<string, string>>({})
	const [comentario, setComentario] = useState<string>("")
	const [ocupado, setOcupado] = useState<boolean>(false)
	/** El alumno que se pidió al último: una respuesta que llega tarde de otro alumno se ignora. */
	const pedido = useRef<number | null>(null)
	/** Puntos o comentario escritos sin guardar: al volver a la pestaña no se recarga el alumno (se perderían). */
	const [sucio, setSucio] = useState<boolean>(false)

	const cargar = useCallback(async (): Promise<ResultadosQuiz | null> => {
		try { const r = await apiQ.results(postId); setRes(r); setError(null); return r } catch (e) { setError(mensajeDeError(e, "No pudimos abrir los resultados.")); return null }
	}, [apiQ, postId])

	const llenar = (d: IntentosDeAlumno, idIntento?: number | null) => {
		setDet(d)
		const entregados = d.attempts.filter((a) => a.state !== "en_curso")
		const elegido = entregados.find((a) => a.id === idIntento) || entregados.find((a) => a.toGrade > 0) || entregados.find((a) => a.counts) || entregados[0] || null
		setIntento(elegido ? elegido.id : null)
		const p: Record<string, string> = {}
		d.attempts.forEach((a) => (a.questions || []).forEach((q) => { if (q.type === "parrafo" && q.earned !== null && q.earned !== undefined && !q.needsGrading) p[a.id + ":" + q.id] = puntosTxt(q.earned) }))
		setPuntos(p)
		setComentario(d.grade?.comment || "")
		setSucio(false)
	}
	const abrir = useCallback(async (studentId: number, idIntento?: number | null, recargar = false) => {
		pedido.current = studentId
		if (!recargar) { setSel(studentId); setDet(null); setAviso(null) }
		try {
			const d = await apiQ.student(postId, studentId)
			if (pedido.current !== studentId) return
			llenar(d, idIntento)
		} catch (e) { if (pedido.current === studentId) setAviso(problema(mensajeDeError(e))) }
	}, [apiQ, postId])

	useEffect(() => {
		cargar().then((r) => {
			if (!r || telefono) return
			const primero = r.students.find((s) => s.state === "por_calificar") || r.students.find((s) => s.state === "calificada") || r.students.find((s) => s.attempts > 0) || r.students[0]
			if (primero) abrir(primero.id)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiQ, postId])
	useAlVolver(() => { cargar(); if (sel !== null && !ocupado && !sucio) abrir(sel, intento, true) })

	if (error && !res) return <div className="pc"><div className="pcPagina"><button type="button" className="pcEnlaceBtn" onClick={onCerrar}>Volver</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (!res) return <div className="pc"><div className="pcCargando">Cargando…</div></div>

	const t = res.task
	const s = res.summary
	const alumno: AlumnoDelQuiz | undefined = res.students.find((x) => x.id === sel)
	const at: IntentoPersonal | undefined = det?.attempts.find((a) => a.id === intento)
	const parrafos = (at?.questions || []).filter((q) => q.type === "parrafo")
	const llaveP = (qid: number) => (at ? at.id + ":" + qid : "")

	const siguiente = (r: ResultadosQuiz, actual: number): number | null => {
		const pend = r.students.filter((x) => (x.state === "por_calificar" || x.state === "calificada") && x.id !== actual)
		if (pend.length === 0) return null
		const i = r.students.findIndex((x) => x.id === actual)
		const pc = pend.filter((x) => x.state === "por_calificar")
		const lista = pc.length > 0 ? pc : pend
		return (lista.find((x) => r.students.indexOf(x) > i) || lista[0]).id
	}

	const enviar = async (accion: "guardar" | "devolver", irAlSiguiente: boolean) => {
		if (!at || sel === null || ocupado) return
		const valores: Record<string, number> = {}
		for (const q of parrafos) {
			const v = (puntos[llaveP(q.id)] ?? "").trim().replace(",", ".")
			const enBlanco = q.answer === null || (typeof q.answer === "string" && q.answer.trim() === "")
			if (v === "") { if (accion === "devolver" && !enBlanco && q.needsGrading) { setAviso(problema("Califica las preguntas de Párrafo antes de devolver.")); return } continue }
			const n = Number(v)
			if (isNaN(n) || n < 0 || n > q.points) { setAviso(problema("Los puntos de esa pregunta van de 0 a " + puntosTxt(q.points) + ".")); return }
			valores[String(q.id)] = n
		}
		setOcupado(true); setAviso(null)
		try {
			const d = await apiQ.grade(postId, sel, at.id, { points: valores, comment: comentario.trim(), action: accion })
			const r = await cargar()
			if (irAlSiguiente && r) {
				const n = siguiente(r, sel)
				if (n !== null) { await abrir(n); setAviso(exito("Devuelta.")) } else { llenar(d, at.id); setAviso(exito("Devuelta. Ya no quedan quizzes por calificar.")) }
			} else { llenar(d, at.id); setAviso(exito(accion === "devolver" ? "Devuelta" : "Guardada: el alumno aún no la ve.")) }
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	const cerrarOReabrir = async () => {
		const cerrar = !t.closed
		if (cerrar && !window.confirm("¿Cerrar este quiz? Ya nadie podrá contestarlo: los intentos en curso se califican con lo que llevan y quien no lo hizo cuenta 0.")) return
		setOcupado(true); setAviso(null)
		try {
			await apiT.close(postId, cerrar)
			await cargar()
			if (sel !== null) await abrir(sel, intento)
			setAviso(exito(cerrar ? "Quiz cerrado: quien no lo hizo cuenta 0." : "Quiz reabierto: los alumnos pueden volver a contestarlo."))
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	// ---- lista de alumnos
	const fila = (x: AlumnoDelQuiz) => (
		<button key={x.id} type="button" className="tkAlumnoFila" aria-current={sel === x.id ? "true" : undefined} onClick={() => {
			if (sucio && x.id !== sel && !window.confirm("Hay puntos o un comentario sin guardar. ¿Cambiar de alumno y perderlos?")) return
			abrir(x.id); setVista("detalle")
		}}>
			<span className={"pcAv" + (x.state === "falta" ? " tkAvOro" : "")} style={{ width: 32, height: 32, fontSize: 11.5 }} aria-hidden="true">{x.photo ? <img src={x.photo} alt="" /> : x.initials}</span>
			<span className="tkNombre">{x.name}</span>
			{x.attempts > 1 ? <small>{x.attempts} Intentos</small> : null}
			{x.late ? <span className="pcChip pcChipOro" style={{ height: 22, fontSize: 11.5 }}>Tarde</span> : null}
			{x.best !== null && (x.state === "devuelta" || x.state === "calificada") ? <small style={esNotaBaja(x.best, t.points) ? { color: "var(--pc-oro-texto)", fontWeight: 700 } : { fontWeight: 700, color: "var(--pc-tinta)" }}>{puntosTxt(x.best)}</small> : null}
			{x.state === "por_calificar" ? <small>{x.toGrade} Párrafo{x.toGrade === 1 ? "" : "s"}</small> : null}
		</button>
	)
	const listaUi = (
		<nav aria-label="Alumnos" className="pcTarjeta tkLista">
			{GRUPOS.map((g) => {
				const xs = res.students.filter((x) => x.state === g.estado)
				if (xs.length === 0) return null
				return <div key={g.estado}><div className="tkListaTitulo"><span>{g.titulo} · {xs.length}</span></div>{xs.map(fila)}</div>
			})}
			{res.students.length === 0 ? <div className="pcVacio">Este Quiz No Tiene Alumnos</div> : null}
		</nav>
	)

	// ---- centro: los intentos del alumno
	const entregados = (det?.attempts || []).filter((a) => a.state !== "en_curso")
	const enCurso = (det?.attempts || []).find((a) => a.state === "en_curso")
	const centro = det && alumno ? (
		<section aria-label="Intentos" className="tkCentro">
			{telefono ? <button type="button" className="pcEnlaceBtn tkAtras" onClick={() => setVista("lista")}><Icono n="atras" size={18} />Alumnos</button> : null}
			<div className="pcTarjeta">
				<div className="pcFilaAutor">
					<Avatar a={{ name: alumno.name, initials: alumno.initials, photo: alumno.photo }} size={40} />
					<div className="pcAutorTextos">
						<div className="pcAutorNombre">{alumno.name}</div>
						<div className="pcTenue pcChico">{entregados.length === 0 ? (enCurso ? "Lo está contestando ahora" : alumno.state === "falta" ? "No lo hizo" : "Aún no lo intenta") : entregados.length + (entregados.length === 1 ? " Intento Entregado" : " Intentos Entregados") + (alumno.lastSubmittedLabel ? " · Último " + alumno.lastSubmittedLabel : "")}</div>
					</div>
				</div>
				{enCurso && entregados.length > 0 ? <div className="pcNota">Tiene un intento en curso (intento {enCurso.number}).</div> : null}
				{entregados.length === 0 ? (
					alumno.state === "falta" ? <div className="tkAvisoOro">No lo hizo{t.closed ? " y el quiz está cerrado: cuenta 0." : ". Cuenta 0 cuando cierres el quiz."}</div> : null
				) : (
					<>
						{entregados.length > 1 ? (
							<div className="qzIntentos" role="group" aria-label="Intentos">
								{entregados.map((a) => <button key={a.id} type="button" aria-pressed={a.id === intento} onClick={() => setIntento(a.id)}>Intento {a.number}{a.counts ? " · Cuenta" : ""}</button>)}
							</div>
						) : null}
						{at ? (
							<>
								<div className="pcTenue pcChico">
									{["Entregó " + (at.submittedLabel || ""), at.closedByTime ? "Se acabó el tiempo" : null, at.late ? "Tarde " + at.lateDays + (at.lateDays === 1 ? " día" : " días") : null].filter(Boolean).join(" · ")}
								</div>
								<div>{(at.questions || []).map((q, i) => (
									<RespuestaVista key={q.id} q={q} n={i + 1} calificar={q.type === "parrafo" ? { valor: puntos[llaveP(q.id)] ?? "", onCambio: (v) => { setPuntos({ ...puntos, [llaveP(q.id)]: v }); setSucio(true) }, deshabilitado: ocupado } : undefined} />
								))}</div>
								{(at.questions || []).length === 0 ? <div className="pcTenue">Las preguntas de este intento ya no existen.</div> : null}
							</>
						) : null}
					</>
				)}
			</div>
		</section>
	) : <section className="tkCentro"><div className="pcCargando">{sel === null ? "Elige un alumno" : "Cargando…"}</div></section>

	// ---- derecha: la nota
	const sumaParrafos = parrafos.reduce((a, q) => a + (Number((puntos[llaveP(q.id)] ?? "").replace(",", ".")) || 0), 0)
	const bruta = at ? (at.autoPoints || 0) + (parrafos.length > 0 ? sumaParrafos : at.manualPoints || 0) : 0
	const devuelta = !!det?.grade?.returned
	const sinCalificar = parrafos.some((q) => q.needsGrading && (puntos[llaveP(q.id)] ?? "").trim() === "")
	const nombrePila = alumno?.name.split(" ")[0] || ""
	const notaUi = det && alumno && at ? (
		<aside aria-label="Nota" className="tkNota qzNotaFija">
			<div className="pcTarjeta" style={{ padding: 18, gap: 14 }}>
				<div className="tkNotaTotal">
					<span className="pcEtiqueta">Intento {at.number}</span>
					<span className={"tkNumeroGrande" + (!sinCalificar && esNotaBaja(Math.max(0, bruta - at.lateDiscount), t.points) ? " tkNumeroOro" : "")}>{puntosTxt(Math.max(0, Math.round((bruta - at.lateDiscount) * 100) / 100))}</span>
					<span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(t.points)}</span>
				</div>
				<div className="pcTenue pcChico">Automático {puntosTxt(at.autoPoints || 0)}{parrafos.length > 0 ? " + Párrafo " + puntosTxt(sumaParrafos) : ""}{at.lateDiscount > 0 ? " − Tarde " + puntosTxt(at.lateDiscount) : ""}</div>
				{det.grade && det.grade.points !== null ? <div className="pcNota">Nota que cuenta: <b style={{ marginLeft: 4 }}>{puntosTxt(det.grade.points)} / {puntosTxt(t.points)}</b>{det.grade.attemptNumber ? <span className="pcTenue" style={{ marginLeft: 4 }}>(intento {det.grade.attemptNumber})</span> : null}</div> : null}
				<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="rqComentario">Comentario Para {nombrePila}</label>
					<textarea id="rqComentario" className="pcTextarea" rows={3} maxLength={2000} value={comentario} onChange={(e) => { setComentario(e.target.value); setSucio(true) }} /></div>
				<LineaAviso a={aviso} />
				<div className="tkBotonesNota">
					{devuelta ? (
						<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => enviar("devolver", false)}>Guardar Cambios</button>
					) : (
						<>
							<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => enviar("devolver", true)}>Devolver Y Siguiente</button>
							<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => enviar("guardar", false)}>Guardar</button>
						</>
					)}
				</div>
				<div className="pcTenue pcChico">{devuelta ? "Ya la ve el alumno." : "El alumno aún no la ve."}{entregados.length > 1 ? " Cuenta el mejor intento." : ""}</div>
			</div>
		</aside>
	) : null

	// ---- vista Preguntas
	const preguntasUi = (
		<section className="pcTarjeta" style={{ padding: 0, gap: 0, width: "100%" }} aria-label="Acierto por pregunta">
			{res.questions.length === 0 ? <div className="pcVacio" style={{ padding: 18 }}>Este Quiz Aún No Tiene Preguntas</div> : null}
			{res.questions.map((q) => {
				const oro = q.correctPct !== null && q.correctPct < 50
				return (
					<div className="qzBarraPregunta" key={q.id}>
						<div>
							<span className="qzMiniNumero" aria-hidden="true">{q.order}</span>
							<span className="qzTextoCorto">{q.text}</span>
							<span className={"qzPct" + (oro ? " qzOro" : "")}>{q.correctPct === null ? "–" : pctTxt(q.correctPct)}</span>
						</div>
						<div className="qzRiel" role="img" aria-label={q.correctPct === null ? "Nadie la ha contestado" : pctTxt(q.correctPct) + " de acierto"}><div className={oro ? "qzRielOro" : undefined} style={{ width: Math.max(0, Math.min(100, q.correctPct || 0)) + "%" }} /></div>
						<div className="pcTenue pcChico">{[TIPO_PREGUNTA[q.type], puntosTxt(q.points) + " Pts", q.answered === 1 ? "1 Respuesta" : q.answered + " Respuestas", q.avgPoints !== null ? "Promedio " + puntosTxt(q.avgPoints) + " Pts" : null].filter(Boolean).join(" · ")}</div>
					</div>
				)
			})}
		</section>
	)

	const mostrarLista = !telefono || vista === "lista"
	const mostrarDetalle = !telefono || vista === "detalle"
	return (
		<div className="pc">
			<header className="pcCabeza">
				<button type="button" className="pcIb" aria-label="Volver" onClick={onCerrar}><Icono n="atras" size={22} /></button>
				<div className="pcCabezaTitulo"><div className="pcT" style={{ overflowWrap: "anywhere" }}>{t.title}</div>
					<div className="pcTenue pcChico">{[cursoTitulo, "Quiz", puntosTxt(t.points) + " Pts", t.dueLabel ? "Vence " + t.dueLabel : null].filter(Boolean).join(" · ")}{t.closed ? <> · <span className="pcChip pcChipOro" style={{ height: 22, fontSize: 11.5 }}>Cerrado</span></> : null}</div></div>
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => cargar()}>Actualizar</button>
				{!t.inherited ? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={onEditar}>Editar Preguntas</button> : null}
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={cerrarOReabrir}>{t.closed ? "Reabrir Quiz" : "Cerrar Quiz"}</button>
			</header>
			<main className="tkCalificar">
				<div className="qzResumen">
					<div className="qzDato"><div>Entregaron</div><div>{s.submitted} <span className="pcTenue" style={{ fontSize: 15 }}>/ {s.students}</span></div></div>
					<div className="qzDato"><div>Promedio</div><div className={s.averagePct !== null && esNotaBaja(s.averagePct, 100) ? "qzOro" : undefined}>{pctTxt(s.averagePct)}</div></div>
					<div className="qzDato"><div>Por Calificar</div><div style={s.toGrade > 0 ? { color: "var(--pc-acento)" } : undefined}>{s.toGrade}</div></div>
					<div className="qzDato"><div>Contestando Ahora</div><div>{s.inProgress}</div></div>
				</div>
				{t.inherited ? <div className="pcNota" style={{ width: "100%" }}>Este quiz viene del curso: sus preguntas se cambian en Catálogo › Cursos › Contenido.</div> : null}
				<div style={{ display: "flex", width: "100%", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
					<div className="qzVistas" role="group" aria-label="Ver">
						<button type="button" aria-pressed={vistaGeneral === "alumnos"} onClick={() => setVistaGeneral("alumnos")}>Alumnos</button>
						<button type="button" aria-pressed={vistaGeneral === "preguntas"} onClick={() => setVistaGeneral("preguntas")}>Preguntas</button>
					</div>
					{vistaGeneral === "preguntas" ? <span className="pcTenue pcChico">La más fallada arriba. Cuenta el intento que cuenta de cada alumno.</span> : null}
				</div>
				{aviso && !notaUi ? <LineaAviso a={aviso} style={{ width: "100%" }} /> : null}
				{vistaGeneral === "preguntas" ? preguntasUi : (
					<>
						{mostrarLista ? listaUi : null}
						{mostrarDetalle ? <>{centro}{notaUi}</> : null}
					</>
				)}
			</main>
		</div>
	)
}

export default ResultadosQuizPantalla
